import { Button, Modal, Select, TextInput } from 'flowbite-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from '../../../Reducers/store';
import {
  formatDateInputs,
  formatNumbers,
  onlyNumberInput,
} from '../../../Utils';
import {
  fetchInventoryTypes,
  fetchStates,
  fetchUsers,
  fetchVendors,
} from '../../../Actions/CommonFields';
import InventoryApi from '../../../Api/InventoryApi';
import { InventoryDailyPrice } from '../../../Models/InventoryDailyPrice';

export default function InventoryDailyPriceModal({
  show,
  setShow,
  getInventoryDailyPrices,
  selectedInventoryPrice,
}: {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  getInventoryDailyPrices: () => Promise<void> | undefined;
  selectedInventoryPrice: InventoryDailyPrice | null;
}) {
  const dispatch = useAppDispatch();
  const { users, inventoryTypes, vendors, states } = useAppSelector(
    (state) => state.commonFields
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);

  const [formData, setFormData] = useState<InventoryDailyPrice>({
    id: 0,
    dateOfPrice: new Date().toISOString().split('T')[0],
    inventoryTypeId: null,
    currentPrice: null,
    addedBy: null,
    dateCreated: null,
    stateId: null,

    inventoryType: null,
    state: null,
  });

  const getReferences = async () => {
    if (
      !users.length ||
      !inventoryTypes.length ||
      !vendors.length ||
      !states.length
    ) {
      await Promise.all([
        dispatch(fetchUsers()),
        dispatch(fetchInventoryTypes()),
        dispatch(fetchVendors()),
        dispatch(fetchStates()),
      ]);
    }
  };

  useEffect(() => {
    if (show === true) {
      getReferences();
    }
  }, [show]);

  const handleSetInitialValues = () => {
    const values = selectedInventoryPrice
      ? {
          ...selectedInventoryPrice,
        }
      : {
          id: 0,
          dateOfPrice: new Date().toISOString().split('T')[0],
          inventoryTypeId: null,
          currentPrice: null,
          addedBy: null,
          dateCreated: null,
          stateId: null,

          inventoryType: null,
          state: null,
        };
    setFormData(values as InventoryDailyPrice);
  };

  useEffect(() => {
    handleSetInitialValues();
  }, [selectedInventoryPrice]);

  const handleResetFormValues = () => {
    const values = {
      id: 0,
      dateOfPrice: new Date().toISOString().split('T')[0],
      inventoryTypeId: null,
      currentPrice: null,
      addedBy: null,
      dateCreated: null,
      stateId: null,

      inventoryType: null,
      state: null,
    };
    setFormData(values as InventoryDailyPrice);
    setButtonDisabled(false);
  };

  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };
  const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };
  const handleChangeCurrencyInput = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    const formattedValue = formatNumbers(value);
    setFormData((prevData) => ({ ...prevData, [name]: formattedValue }));
  };

  function cleanMoneyValues(formData: InventoryDailyPrice) {
    formData.currentPrice = Number(
      (formData.currentPrice ?? 0).toString().replace(',', '')
    );
  }
  const onSubmit = async (e: React.ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();
    setButtonDisabled(true);
    setIsLoading(true);
    cleanMoneyValues(formData);
    if (!formData.dateOfPrice) {
      formData.dateOfPrice = null;
    }
    return await InventoryApi.addUpdateInventoryDailyPrice(formData)
      .then((res) => {
        if (res.data.success) {
          toast.success('Save Success');
          setIsLoading(false);
          setButtonDisabled(false);
          handleResetFormValues();
          getInventoryDailyPrices();
          onCloseModal();
        } else {
          toast.error(res.data.message);
          setIsLoading(false);
          setButtonDisabled(false);
        }
      })
      .catch((err) => {
        toast.error('Error: ' + err);
        setIsLoading(false);
        setButtonDisabled(false);
      });
  };

  function onCloseModal() {
    handleResetFormValues();
    setShow(false);
  }

  return (
    <Modal show={show} size='2xl' onClose={onCloseModal} popup>
      <Modal.Header></Modal.Header>
      <Modal.Body>
        <div className='w-full'>
          <div className='w-full'>
            <div className='mx-1 flex items-end justify-center'>
              <div className='flex flex-1 justify-center'>
                <h1 className='text-gray-900 font-bold text-4xl'>
                  {selectedInventoryPrice ? 'Edit' : 'Add'} Inventory Price
                </h1>
              </div>
            </div>
          </div>
          <div>
            <div className='container mx-auto p-2'>
              <div className='md:flex no-wrap md:-mx-2 '>
                <div className='w-full'>
                  <div className='bg-white'>
                    <hr className='bg-gray-300 h-[5px]'></hr>
                    <form id='vendorInventoryForm' onSubmit={onSubmit}>
                      <div className='text-gray-500'>
                        <div className='grid grid-cols-[35%_65%] text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Date:
                            </div>
                          </div>
                          <div className='w-full'>
                            <TextInput
                              id='dateOfPrice'
                              name='dateOfPrice'
                              type='date'
                              placeholder='Date...'
                              onChange={handleChangeInput}
                              value={formData.dateOfPrice?.toString()}
                              sizing='md'
                              pattern='\d{4}-\d{2}-\d{2}'
                              required
                            />
                          </div>
                        </div>
                        <hr className='bg-gray-300 h-[2px]'></hr>
                        <div className='grid grid-cols-[35%_65%] text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Type:
                            </div>
                          </div>
                          <div className='w-full'>
                            <Select
                              id='inventoryTypeId'
                              name='inventoryTypeId'
                              onChange={handleChangeSelect}
                              value={formData.inventoryTypeId ?? ''}
                              sizing='md'
                              required
                            >
                              <option value={''}>(Select)</option>
                              {inventoryTypes.map((type) => (
                                <option key={type.id} value={type.id}>
                                  {type.name}
                                </option>
                              ))}
                            </Select>
                          </div>
                        </div>
                        <hr className='bg-gray-300 h-[2px]'></hr>
                        <div className='grid grid-cols-[35%_65%] text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Price:
                            </div>
                          </div>
                          <div className='w-full'>
                            <TextInput
                              id='currentPrice'
                              name='currentPrice'
                              type='text'
                              placeholder='0.00'
                              onChange={handleChangeCurrencyInput}
                              onKeyDown={(event) => {
                                if (onlyNumberInput(event)) {
                                  event.preventDefault();
                                }
                              }}
                              value={formData.currentPrice ?? ''}
                              sizing='md'
                              addon='$'
                              required
                            />
                          </div>
                        </div>
                        <hr className='bg-gray-300 h-[2px]'></hr>
                        <div className='grid grid-cols-[35%_65%] text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              State:
                            </div>
                          </div>
                          <div className='w-full'>
                            <Select
                              id='stateId'
                              name='stateId'
                              onChange={handleChangeSelect}
                              value={formData.stateId ?? ''}
                              sizing='md'
                              required
                            >
                              <option value={''}>(State)</option>
                              {states.map((state) => (
                                <option key={state.id} value={state.id}>
                                  {state.name}
                                </option>
                              ))}
                            </Select>
                          </div>
                        </div>
                      </div>
                      <div className='flex justify-end pt-2'>
                        <Button
                          disabled={buttonDisabled}
                          type='submit'
                          className='buttonMain'
                          form='vendorInventoryForm'
                          // onClick={() => onSubmit()}
                        >
                          Save
                        </Button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}
